import { Button } from '@factorialco/deprecated-design-system'
import toQueryParams from 'jquery-param'
import type { $Values } from 'utility-types'

import { observer } from 'mobx-react'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'
import { apiClient as ApiClient } from 'modules/core/lib/mobx-rest'
import app from 'modules/core/store/models/app'

import type { AnalyticsFilterForm } from 'modules/reviews/hooks/useAnalyticsMultiFilters'
import { useAnalytics } from 'modules/reviews/pages/performance/Show/Analytics/analyticsContext'
import type Review from 'modules/reviews/store/models/v2/Review'

interface ButtonProps {
  form: Review
}

const EXPORT_FORMATS = {
  csv: 'csv',
  xlsx: 'xlsx',
}

class DataParams {
  [key: string]: number | number[] | string
}

const applyFilterToParams = (
  params: DataParams,
  reviewAnalyticsFilter: AnalyticsFilterForm | undefined
) => {
  if (reviewAnalyticsFilter?.isActive && reviewAnalyticsFilter?.default?.length) {
    const simpleFilterValues = reviewAnalyticsFilter.default[0]?.values

    if (simpleFilterValues.field === 'team') {
      params.teams = [simpleFilterValues.value]
    } else if (simpleFilterValues.field === 'location') {
      params.locations = [simpleFilterValues.value]
    }
  }

  return params
}

const AnalyticsActions = ({ form }: ButtonProps) => {
  const { reviewAnalyticsFilter } = useAnalytics()
  const { employee } = useCurrentSession()

  if (!form.answeredQuestionsCount) return null

  const onDownload = async (exportFormat: $Values<typeof EXPORT_FORMATS>) => {
    const params: DataParams = {
      form_id: form.id,
      export_format: exportFormat,
    }

    const paramsWithFilters = applyFilterToParams(params, reviewAnalyticsFilter)

    const url = buildUrl(`/surveys/exports/download?${toQueryParams(paramsWithFilters)}`)
    window.location.href = url
  }

  const onDownloadPDF = async () => {
    const params: DataParams = {
      form_id: form.id,
    }

    const paramsWithFilters = applyFilterToParams(params, reviewAnalyticsFilter)

    const pdfExportsDownloadable = `/surveys/pdf_exports/downloadable?${toQueryParams(
      paramsWithFilters
    )}`

    const api = ApiClient()
    let response = null

    try {
      const payload = await api.get(pdfExportsDownloadable)
      response = payload.result
      app.setFlash({
        type: 'correct',
        message: i18n.t('surveys.review.reviews.pdf_successfully_requested'),
        time: 4000,
      })
    } catch (_error) {}

    return response
  }

  return (
    <PopoverMenu type="list">
      <PopoverMenu.Trigger>
        <Button.Decorative type="secondary" iconName="ellipsis" />
      </PopoverMenu.Trigger>
      <PopoverMenu.Content side="bottom" align="start">
        {/* TODO: PDF only available for Employees with 'My Document' folder, until further Product decision about building our own Document Space for Performance */}
        {!!employee && (
          <PopoverMenu.TouchableOption
            title={i18n.t('surveys.review.reviews.download_pdf')}
            onClick={onDownloadPDF}
          />
        )}
        <PopoverMenu.TouchableOption
          title={i18n.t('surveys.review.reviews.download_csv')}
          onClick={() => onDownload(EXPORT_FORMATS.csv)}
        />
        <PopoverMenu.TouchableOption
          title={i18n.t('surveys.review.reviews.download_xlsx')}
          onClick={() => onDownload(EXPORT_FORMATS.xlsx)}
        />
      </PopoverMenu.Content>
    </PopoverMenu>
  )
}

export default observer(AnalyticsActions)
